import {
  getCandidates,
  createCandidate,
  bulkImportCandidates,
  resendCandidateEmail,
  updateCandidateStatus,
  deleteCandidate,
} from '../../shared/services/apiService';

// ── CSV preview (same row parser as question import; quoted fields + newlines) ─
const parseCSVRows = (text) => {
  let t = String(text).replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  const pushField = () => {
    row.push(field.trim());
    field = '';
  };
  const pushRow = () => {
    if (row.length && row.some(c => c !== '')) rows.push(row);
    row = [];
  };

  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (inQuotes) {
      if (ch === '"') {
        if (t[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { pushField(); continue; }
    if (ch === '\n') { pushField(); pushRow(); continue; }
    if (ch === '\r') {
      if (t[i + 1] === '\n') i++;
      pushField(); pushRow(); continue;
    }
    field += ch;
  }
  pushField();
  if (row.length && row.some(c => c !== '')) rows.push(row);

  return rows;
};

const normHeader = (h) => String(h || '').trim().toLowerCase().replace(/\s+/g, '_');

/** Parse CSV text for bulk candidate import preview (must match sample format). */
export const parseCandidatesCsvText = (text) => {
  const allRows = parseCSVRows(text);
  if (allRows.length < 1) throw new Error('File is empty');

  const headers = allRows[0].map(normHeader);
  if (!headers.includes('full_name')) throw new Error('Missing required column: full_name');
  if (!headers.includes('email')) throw new Error('Missing required column: email');

  const rows = [];
  for (let i = 1; i < allRows.length; i++) {
    const values = allRows[i];
    if (values.every(v => !String(v || '').trim())) continue;
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = String(values[idx] ?? '').trim(); });
    rows.push(obj);
  }
  if (rows.length === 0) throw new Error('No data rows found in CSV');
  return { headers, rows };
};

export const readCandidatesCsvPreview = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try { resolve(parseCandidatesCsvText(e.target.result)); }
      catch (err) { reject(err instanceof Error ? err : new Error(String(err))); }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

export const isCandidatePreviewRowInvalid = (row) =>
  !row.full_name?.trim() || !row.email?.trim();

// ── Load all candidates ───────────────────────────────────────────────────────
export const loadCandidates = async () => {
  const res = await getCandidates();
  return res.data.data.candidates;
};

// ── Add a single candidate (returns created candidate info) ──────────────────
export const addCandidate = async (formData) => {
  const res = await createCandidate(formData);
  return res.data.data;
};

// ── Bulk CSV import ───────────────────────────────────────────────────────────
export const importCandidatesFromCsv = async (file, testConfigId) => {
  const fd = new FormData();
  fd.append('file', file);
  if (testConfigId) fd.append('test_config_id', testConfigId);
  const res = await bulkImportCandidates(fd);
  return res.data.data;
};

// ── Resend credentials email (returns { username, newPassword, email }) ───────
export const resendEmail = async (userId) => {
  const res = await resendCandidateEmail(userId);
  return res.data.data;
};

// ── Toggle active/inactive status ────────────────────────────────────────────
export const toggleStatus = async (userId, isCurrentlyActive) => {
  await updateCandidateStatus(userId, { is_active: !isCurrentlyActive });
};

// ── Delete candidate permanently ─────────────────────────────────────────────
export const removeCandidate = async (userId) => {
  await deleteCandidate(userId);
};
